import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import notificationService from "@/util/notificationService";
import { getRefListNameByID } from "@/util/common_function";

// Mirrors legacy InmateTrackingInterface.asp's HandleValidateDataModule: saving
// a movement with these Inmate_Location/Loan_Type combinations auto-creates
// Transport record(s) as a side effect. In legacy this ran client-side; we
// replicate the same trigger conditions here.
const isPermHousingOrTransportMove = (entry) =>
  entry?.inmateLocation === "Perm Housing Change" ||
  entry?.inmateLocation === "Transport";

// The Loan Type field is a Loan_Type ref-list value, but callers don't agree
// on its shape: some forms (e.g. Housing and Tracking's Loan tab) submit the
// numeric refListItemGlobalId, while others (Batch Movement, already-saved
// records refetched from the server) submit the resolved display string
// directly. Normalize to the name so callers can compare either shape.
const getLoanTypeName = (loanType) =>
  typeof loanType === "number"
    ? getRefListNameByID("Loan_Type", loanType) || ""
    : loanType || "";

const isCourtLoanMove = (entry) => {
  if (entry?.inmateLocation !== "Loan") return false;
  const loanTypeName = getLoanTypeName(entry?.loanType);
  return loanTypeName === "Court" || loanTypeName.startsWith("T-");
};

const isTransportTriggeringMovement = (entry) =>
  isPermHousingOrTransportMove(entry) || isCourtLoanMove(entry);

// Only newly-added movements (no inmateTrackingID yet) should trigger a new
// Transport record — re-saving an already-created movement must not create
// duplicates. Returns each matching entry alongside its index in the array,
// so callers can re-associate it with the real inmateTrackingID once the
// server assigns one.
export const getNewTransportTriggeringEntries = (inmateTracking) =>
  (inmateTracking || [])
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => !entry?.inmateTrackingID && isTransportTriggeringMovement(entry));

// For save flows where a tracking record may be created server-side as a
// side effect of a different endpoint (e.g. Charge_create/update creating an
// InmateTracking row from a Court date), we can't tell "new" from "existing"
// by a missing ID up front. Instead, diff the freshly-refetched tracking
// array against the set of IDs that existed before this save.
export const getTriggeringEntriesFromDiff = (freshTracking, previousTrackingIds) =>
  (freshTracking || []).filter(
    (entry) =>
      entry?.inmateTrackingID &&
      !previousTrackingIds?.has(entry.inmateTrackingID) &&
      isTransportTriggeringMovement(entry),
  );

// True if this single tracking record (already known to be newly submitted,
// e.g. inmateTrackingID: null) matches a Transport-triggering movement type.
export const isNewTransportTriggeringMovement = (entry) =>
  !entry?.inmateTrackingID && isTransportTriggeringMovement(entry);

// For save flows where the full tracking history isn't available client-side
// to index- or diff-match against (e.g. Batch Movement, which only submits
// the one new record per inmate), find the freshly-refetched record that
// corresponds to what was just submitted by matching on content rather than
// ID or array position.
export const matchResyncedEntry = (freshTracking, submittedEntry) => {
  const candidates = (freshTracking || []).filter(
    (entry) =>
      entry?.inmateTrackingID &&
      isTransportTriggeringMovement(entry) &&
      entry?.inmateLocation === submittedEntry?.inmateLocation &&
      entry?.scheduledDate === submittedEntry?.scheduledDate &&
      entry?.scheduledTime === submittedEntry?.scheduledTime &&
      entry?.moveReason === submittedEntry?.moveReason,
  );
  if (!candidates.length) return null;
  return candidates.reduce((latest, entry) =>
    entry.inmateTrackingID > latest.inmateTrackingID ? entry : latest,
  );
};

// Transport.save expects cdInvolvements nested under jackets[0], not as a
// root-level field — matches the shape TransportDetails.jsx's Full Search
// payload and buildWarrantPayload.js's per-record jackets use.
const attachCdInvolvements = (jackets, cdInvolvements) => {
  const list = jackets ?? [];
  if (!list.length) return list;
  const [first, ...rest] = list;
  return [{ ...first, cdInvolvements: cdInvolvements ?? [] }, ...rest];
};

// context: { jacketID, bioID, creatorStaffID, housingAgency, jackets, bio, cdInvolvements }
export const buildTransportPayloads = (trackingEntry, context) => {
  const base = {
    transportID: 0,
    jacketID: context.jacketID,
    bioID: context.bioID,
    inmateTrackingID: trackingEntry.inmateTrackingID,
    creatorStaffID: context.creatorStaffID,
    housingAgency: context.housingAgency,
    transportOfficerStaffID: trackingEntry.transportingOfficerStaffID,
    moveReason: trackingEntry.moveReason,
    comments: trackingEntry.comments,
    transportStatus: "Pending",
    jackets: attachCdInvolvements(context.jackets, context.cdInvolvements),
    bio: context.bio ?? [],
  };

  if (isPermHousingOrTransportMove(trackingEntry)) {
    // Same rule as the Loan/Court branch below: Complete movements only
    // populate Actual date/time (Scheduled is cleared when the movement is
    // saved as Complete), so Transport needs the Actual value in that case.
    const isComplete = trackingEntry.movementStatus === "Complete";
    return [
      {
        ...base,
        inJail: true,
        fromLocation: context.housingAgency,
        toLocation: trackingEntry.transportDestination,
        scheduledDate: isComplete ? trackingEntry.actualDate : trackingEntry.scheduledDate,
        scheduledTime: isComplete ? trackingEntry.actualTime : trackingEntry.scheduledTime,
      },
    ];
  }

  // Loan/Court — round trip: outbound to the court/destination, then the
  // return leg back to the housing agency. Legacy uses the Actual date/time
  // for the outbound leg when the movement is saved as Complete (Scheduled
  // date/time is only populated/visible while the movement is Pending).
  const destination = trackingEntry.court || trackingEntry.loanDestination;
  const isComplete = trackingEntry.movementStatus === "Complete";
  return [
    {
      ...base,
      inJail: true,
      fromLocation: context.housingAgency,
      toLocation: destination,
      scheduledDate: isComplete ? trackingEntry.actualDate : trackingEntry.scheduledDate,
      scheduledTime: isComplete ? trackingEntry.actualTime : trackingEntry.scheduledTime,
    },
    {
      ...base,
      inJail: true,
      fromLocation: destination,
      toLocation: context.housingAgency,
      scheduledDate: trackingEntry.expectedBackDate,
      scheduledTime: trackingEntry.expectedBackTime,
    },
  ];
};

// Best-effort: the booking has already been saved successfully by the time
// this runs, so a failure here must surface as its own error rather than as
// a booking-save failure. Payloads are saved one at a time (not in parallel)
// so the outbound leg is always inserted — and gets a lower transportID —
// before the return leg; concurrent requests would race and could let the
// return leg land first.
export const createTransportsForNewMovements = (payloads) =>
  payloads.reduce(
    (chain, payload) =>
      chain.then(() =>
        api
          .post(API_ENDPOINTS.transport.save, payload)
          .catch(() =>
            notificationService.error(
              "Error",
              "Failed to auto-create a Transport record for this movement.",
            ),
          ),
      ),
    Promise.resolve(),
  );

// Mirrors legacy BookingsInterface.vbs's CancelPendingTransports: releasing an
// inmate with pending movements cancels their linked Transport record(s) too.
// The search result rows don't carry the fields Transport.save requires
// (jacketID, bio, etc.), so each match is re-fetched in full before being
// re-saved with just transportStatus flipped — updating from the partial
// search row could otherwise wipe those fields.
export const cancelPendingTransportsForJacket = (jacketID) => {
  if (!jacketID) return Promise.resolve();

  const searchPayload = [
    { filterKey: "jacketID", value: String(jacketID) },
    { filterKey: "transportStatus", value: "Pending" },
    { filterKey: "searchType", value: "Starts With" },
  ];
  const searchUrl = `${API_ENDPOINTS.transport.search}?page=0&size=100&top=100&direction=ASC&orderBy=transportID`;

  return api
    .post(searchUrl, searchPayload)
    .then((res) => {
      const pendingRows = (res?.data?.rows || []).filter(
        (row) => row?.transportStatus === "Pending",
      );
      return Promise.all(
        pendingRows.map((row) =>
          api
            .get(`${API_ENDPOINTS.transport.getByID}${row.transportID}`)
            .then((fullRes) => {
              const fullRecord = fullRes?.data;
              if (!fullRecord) return;
              return api.post(API_ENDPOINTS.transport.save, {
                ...fullRecord,
                transportStatus: "Cancelled",
              });
            }),
        ),
      );
    })
    .catch(() =>
      notificationService.error(
        "Error",
        "Failed to cancel this inmate's pending Transport record(s) on release.",
      ),
    );
};
